/**
 * PWAInstallPrompt — shows a small "Install HospiceGuard" pill when the
 * browser fires beforeinstallprompt. Dismissable, doesn't nag.
 */
import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Smartphone, X } from 'lucide-react';

const DISMISSED_KEY = 'hg-pwa-install-dismissed';

const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-sm bg-slate-900 text-white rounded-xl shadow-2xl shadow-emerald-500/20 border border-emerald-500/30 p-4 flex items-center gap-3"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
        <Smartphone className="w-5 h-5 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Install HospiceGuard</p>
        <p className="text-xs text-slate-400">
          Add to your home screen for one-tap visit notes.
        </p>
      </div>
      <Button
        size="sm"
        onClick={install}
        className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
        data-testid="pwa-install-btn"
      >
        Install
      </Button>
      <button
        onClick={dismiss}
        className="text-slate-500 hover:text-white"
        aria-label="Dismiss"
        data-testid="pwa-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
